/**
 * ATL-2001 — which business day each store's "today" is, for the Supabase daily-stats feed.
 *
 * `dailyStatsAtom` holds ONE entry per store: today's. In Firestore read mode "today" is the
 * document `getDailyStatID(storeId, new Date())`, i.e. the store's BUSINESS day — its local date,
 * moved back one day while the local time is before the store's autoclose cutoff. The Supabase
 * feed (`/reports/daily-stats` + `/reports/shift`, both bounded by `date`-typed business days,
 * `business_date_for`) used to be asked for the DEVICE's calendar day instead. For a store that
 * closes at 04:00 every instant between 00:00 and 04:00 therefore asked for the wrong day: a shift
 * closed at 00:04 belongs to business day D, the refetch asked for D+1, found no row, and a fresh
 * session showed «Откройте смену» instead of the closed shift's summary (Justore Михайловка, after
 * its 2026-10-25 read cutover).
 *
 * The day is decided per store by `businessDateForStore` — the same function `getDailyStatID`
 * reaches through `getStoreBusinessDate` — so the two read modes cannot disagree about it. Stores
 * of one organization may sit in different timezones or close at different hours, so at a given
 * instant they can be on different business days; the plan groups them by day and the caller
 * issues one single-day request per distinct day (the operational window on the backend admits
 * only single-day requests for roles without a report permission —
 * `operational-daily-stats-window.util.ts`).
 *
 * A request for day X is organization-wide, so it also returns rows of stores whose today is NOT
 * X (yesterday's row of a store that is already past its cutoff, say). `belongsToToday` is the
 * filter: a row is kept only for the store whose business day it is.
 */
import { businessDateForStore, businessDayStringForStore, type StoreWithTimezone } from '../../utils/timezoneUtils'

export interface BusinessDayRequest {
  /** `YYYY-MM-DD` — the business day, as the endpoints spell their bound. */
  businessDay: string
  /** The same day as a Date whose LOCAL calendar fields name it (what the fetch helpers format). */
  date: Date
  /** The store keys (Firestore ids) whose today this is. */
  storeKeys: string[]
}

export interface DailyStatsBusinessDayPlan {
  requests: BusinessDayRequest[]
  /** The store's today, or the device's calendar day for a store it does not know. */
  businessDayOf: (storeKey: string) => string
  /** Whether a row of `storeKey` for `businessDay` is that store's today. */
  belongsToToday: (storeKey: string, businessDay: string) => boolean
}

/**
 * @param stores the store records by Firestore id (the key space of `dailyStatsAtom`).
 * @param storeKeys the stores to plan for; every key of `stores` is always included too, because
 *   the requests are organization-wide and every store they return needs a day to be tested against.
 * @param now the ONE instant the whole fetch is about — taken once, so two stores cannot be
 *   planned on either side of a cutoff tick.
 */
export function planDailyStatsBusinessDays(
  stores: Readonly<Record<string, StoreWithTimezone | undefined>>,
  storeKeys: readonly string[],
  now: Date
): DailyStatsBusinessDayPlan {
  const businessDayOf = (storeKey: string): string => businessDayStringForStore(stores[storeKey], now)

  const byDay = new Map<string, BusinessDayRequest>()
  const keys = [...new Set([...Object.keys(stores), ...storeKeys])]
  // No store at all (stores not loaded yet): still ask for the device's own day, as before.
  if (keys.length === 0) {
    const date = businessDateForStore(undefined, now)
    const businessDay = businessDayStringForStore(undefined, now)
    byDay.set(businessDay, { businessDay, date, storeKeys: [] })
  }
  for (const key of keys) {
    const businessDay = businessDayOf(key)
    const existing = byDay.get(businessDay)
    if (existing) {
      existing.storeKeys.push(key)
    } else {
      byDay.set(businessDay, { businessDay, date: businessDateForStore(stores[key], now), storeKeys: [key] })
    }
  }

  return {
    requests: [...byDay.values()].sort((a, b) => a.businessDay.localeCompare(b.businessDay)),
    businessDayOf,
    belongsToToday: (storeKey, businessDay) => businessDayOf(storeKey) === businessDay,
  }
}

/** `fetchDailyStatsFromSupabase`'s answer: stats by response store key, then by business day. */
export type DailyStatsByStore = Record<string, Record<string, Record<string, unknown>>>

/** One `/reports/shift` row as `fetchShiftReportFromSupabase` returns it. */
export interface ShiftReportRow {
  storeId?: string | null
  date?: string
  stats?: Record<string, unknown> | null
}

export interface TodayDailyStat {
  storeKey: string
  /** The row's business day key, as the response named it. */
  dateKey: string
  stat: Record<string, unknown>
}

/**
 * Today's daily-stats rows, one single-day request per distinct business day in `plan`, each row
 * kept only for the store whose today it is. A failed request rejects the whole read — the caller
 * reports it and the freshness net retries — rather than half-writing the stores that answered.
 *
 * @param storeKeyOf maps a response store key to `dailyStats`' key (Firestore id; ATL-1118 UUID path).
 * @param fetchDay `(date) => fetchDailyStatsFromSupabase(org, null, date, date)`.
 */
export async function readTodayDailyStats(
  plan: DailyStatsBusinessDayPlan,
  storeKeyOf: (responseStoreKey: string) => string,
  fetchDay: (date: Date) => Promise<DailyStatsByStore>
): Promise<TodayDailyStat[]> {
  const answers = await Promise.all(
    plan.requests.map(async request => ({ request, statsDict: await fetchDay(request.date) }))
  )
  const out: TodayDailyStat[] = []
  for (const { request, statsDict } of answers) {
    for (const responseStoreKey of Object.keys(statsDict || {})) {
      const storeKey = storeKeyOf(responseStoreKey)
      // An organization-wide request for day X also returns stores whose today is not X; their
      // entry must stay the one their own day's request writes.
      if (!plan.belongsToToday(storeKey, request.businessDay)) continue
      const storeStats = statsDict[responseStoreKey] || {}
      for (const dateKey of Object.keys(storeStats)) {
        out.push({ storeKey, dateKey, stat: storeStats[dateKey] })
      }
    }
  }
  return out
}

/** `readTodayDailyStats` for the shift report (`/reports/shift`, the same `date`-typed bound). */
export async function readTodayShiftStats(
  plan: DailyStatsBusinessDayPlan,
  storeKeyOf: (responseStoreKey: string) => string,
  fetchDay: (date: Date) => Promise<ShiftReportRow[]>
): Promise<Array<{ storeKey: string; stats: Record<string, unknown> }>> {
  const answers = await Promise.all(
    plan.requests.map(async request => ({ request, rows: await fetchDay(request.date) }))
  )
  const out: Array<{ storeKey: string; stats: Record<string, unknown> }> = []
  for (const { request, rows } of answers) {
    for (const row of rows || []) {
      if (!row?.storeId || !row.stats) continue
      const storeKey = storeKeyOf(row.storeId)
      if (!plan.belongsToToday(storeKey, request.businessDay)) continue
      out.push({ storeKey, stats: row.stats })
    }
  }
  return out
}
